import React, { useState, useEffect, useCallback, useRef } from "react"
import { Form, Row, Col, Select, DatePicker, Input, Switch, Space, Spin } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { useWatch } from "antd/es/form/Form"
import { fetchExperienceById, updateBookingSelections, fetchExperienceBookingDetails, resetExperienceBookingData } from "store/experiences"
import TimeDurationSelector from "./TimeDurationSelector"
import GuestInputGroup from "./GuestInputGroup"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"

const { Option } = Select
const { TextArea } = Input

const ExperienceDetail = ({ form, bookingId, booking, isEdit, mode, filterExperienceIds }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [durationLoading, setDurationLoading] = useState(false)
  const [timeLoading, setTimeLoading] = useState(false)
  const lastSlotsFetchRef = useRef(null)
  const {
    list: experiences,
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  // Filter experiences if filterExperienceIds is provided (optional - backward compatible)
  const filteredExperiences = filterExperienceIds && filterExperienceIds.length > 0
    ? experiences.filter(exp => filterExperienceIds.includes(exp.id))
    : experiences
  const { scheduled_deletion_date } = useSelector((state) => state.user?.guide) || {}
  const accessibilityRequired = useWatch("accessibility_required", form)
  const selectedExperience = useWatch("experience", form)
  const paymentType = useWatch("paymentType", form)
  const date = useWatch("date", form)
  const selectedExperienceId = selectedExperience?.value
  const timeSlots = (experienceBookingData?.bookingData?.data || []).map((item) => ({
    key: item,
    value: item
  }))
  const baseDurations = (date && experienceBookingData?.bookingData?.trip_durations) || []
  const selectedTripLength = bookingSelections?.tripLength
  const isTripLengthIncluded = selectedTripLength ? baseDurations.some((slot) => slot.key === selectedTripLength.key) : true
  const updatedDurationSlots = isEdit && !isTripLengthIncluded ? [...baseDurations, selectedTripLength] : baseDurations

  // Auto-select duration if only one slot is available
  useEffect(() => {
    if (updatedDurationSlots.length === 1) {
      const onlySlot = updatedDurationSlots[0]
      form.setFieldsValue({ duration: onlySlot.value })
      dispatch(updateBookingSelections({ tripLength: onlySlot }))
    }
  }, [updatedDurationSlots, form, dispatch])

  const selectedTimeSlot = `${booking?.start_time} - ${booking?.end_time}`
  const isStringSlotPresent = timeSlots.some((slot) => slot.value === selectedTimeSlot)
  // Only add the old time slot if BOTH the date AND duration match the original booking
  // This prevents showing stale slots when the user changes duration/date
  const shouldAddTimeSlot =
    isEdit &&
    selectedTimeSlot &&
    !isStringSlotPresent &&
    selectedTripLength?.value === booking?.selected_trip_duration &&
    bookingSelections?.date === booking?.booked_date

  const updatedTimeSlots = shouldAddTimeSlot ? [...timeSlots, { key: selectedTimeSlot, value: selectedTimeSlot }] : timeSlots

  // Auto-select time slot if only one is available
  useEffect(() => {
    if (!timeLoading && updatedTimeSlots.length === 1 && !bookingSelections?.timeSlot) {
      const onlySlot = updatedTimeSlots[0]
      form.setFieldsValue({ time: onlySlot.value })
      dispatch(updateBookingSelections({ timeSlot: onlySlot.key }))
    }
  }, [updatedTimeSlots, timeLoading, bookingSelections?.timeSlot, form, dispatch])

  // Handle experience selection
  const handleExperienceChange = (experience) => {
    form.setFieldsValue({ experience, date: "" })
    dispatch(
      updateBookingSelections({
        date: null,
        timeSlot: null
      })
    )
    dispatch(resetExperienceBookingData())
  }

  // Function to disable dates
  const disableDates = useCallback(
    (current) => {
      const date = dayjs(current)
      
      if (date.isBefore(dayjs(), "day")) return false

      const weekdayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      }

      const blockedWeekdays = experienceDetail?.unavailable_weekdays?.map((day) => weekdayMap[day.toLowerCase()]) || []
      const isSoldOut = experienceDetail?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const isUnavailable = experienceDetail?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const hasDateRanges = Array.isArray(experienceDetail?.available_date_ranges) && experienceDetail.available_date_ranges.length > 0

      let isInAvailableRange = true
      if (hasDateRanges) {
        isInAvailableRange = experienceDetail?.available_date_ranges.some((range) => {
          const start = dayjs(range.start).startOf("day")
          const end = dayjs(range.end).endOf("day")
          return date.isBetween(start, end, null, "[]")
        })
      }
      return (
        date.isAfter(scheduled_deletion_date) ||
        blockedWeekdays.includes(date.day()) ||
        isUnavailable ||
        isSoldOut ||
        (hasDateRanges && !isInAvailableRange)
      )
    },
    [experienceDetail]
  )

  // handle date change
  const handleDateChange = useCallback(
    (value) => {
      setDurationLoading(true)
      const formattedDate = dayjs(value).format("YYYY-MM-DD")
      dispatch(
        updateBookingSelections({
          date: formattedDate,
          timeSlot: null,
          tripLength: null
        })
      )
      form.setFieldsValue({ date: value, duration: null })
    },
    [dispatch, bookingSelections.tripLength, selectedExperienceId]
  )

  // handle Trip duration change
  const handleTripDurationChange = useCallback(
    (tripLength) => {
      const isTripDurationUnchanged = tripLength?.value === booking?.selected_trip_duration
      setTimeLoading(true)

      // Recalculate total and breakdown if counts exist
      const counts = bookingSelections?.counts
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const hasGuestCounts = counts && (
        Object.values(counts.main || {}).some(v => v > 0) ||
        Object.values(counts.extra || {}).some(v => v > 0)
      )

      const updatedSelections = {
        tripLength,
        timeSlot: isTripDurationUnchanged ? selectedTimeSlot : null
      }

      if (hasGuestCounts) {
        // Create temporary bookingSelections with new tripLength for calculation
        const tempSelections = { ...bookingSelections, tripLength }
        const { total, breakdown } = calculateBookingTotal(tempSelections, counts, guideFeePercent, {
          paymentType,
          isEdit,
          originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
          customFees: experienceDetail?.assigned_custom_fees,
          platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting
        }) || { total: 0, breakdown: [] }
        updatedSelections.amount = total
        updatedSelections.breakdown = breakdown
      }

      dispatch(updateBookingSelections(updatedSelections))
      form.setFieldsValue({ duration: tripLength?.value, time: isTripDurationUnchanged ? selectedTimeSlot : null })
    },
    [dispatch, selectedExperienceId, bookingSelections, selectedTimeSlot, experienceDetail?.guide_booking_fee_percentage, booking, paymentType]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      form.setFieldsValue({ time: value })
      dispatch(updateBookingSelections({ timeSlot: value }))
    },
    [dispatch]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      // Calculate total and breakdown when counts change
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, breakdown } = calculateBookingTotal(bookingSelections, newCounts, guideFeePercent, {
        paymentType,
        isEdit,
        originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
        customFees: experienceDetail?.assigned_custom_fees,
        platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting
      }) || { total: 0, breakdown: [] }

      dispatch(updateBookingSelections({
        counts: newCounts,
        amount: total,
        breakdown
      }))
    },
    [dispatch, bookingSelections, experienceDetail?.guide_booking_fee_percentage, booking?.booking_fee_percentage, paymentType, isEdit]
  )

  // Recalculate amount and breakdown on mount or when fee percentage or payment type changes, if we have the necessary data
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const currentAmount = bookingSelections?.amount
    const hasGuestCounts = counts && (
      Object.values(counts.main || {}).some(v => v > 0) ||
      Object.values(counts.extra || {}).some(v => v > 0)
    )

    if (hasGuestCounts && tripLength) {
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, breakdown } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
        paymentType,
        isEdit,
        originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
        customFees: experienceDetail?.assigned_custom_fees,
        platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting
      }) || { total: 0, breakdown: [] }

      // Only update if amount is different (to avoid infinite loops)
      if (total !== currentAmount) {
        dispatch(updateBookingSelections({
          amount: total,
          breakdown
        }))
      }
    }
  }, [
    experienceDetail?.guide_booking_fee_percentage,
    experienceDetail?.assigned_custom_fees,
    experienceDetail?.platform_fee_setting,
    booking?.booking_fee_percentage,
    booking?.platform_fee_setting,
    paymentType,
    bookingSelections?.counts,
    bookingSelections?.tripLength,
    bookingSelections?.amount,
    isEdit,
    dispatch,
    bookingSelections
  ])

  useEffect(() => {
    if (selectedExperience?.value) {
      dispatch(fetchExperienceById(selectedExperience.value, "", mode))
    }
  }, [selectedExperience?.value, dispatch, mode])

  useEffect(() => {
    if (date && selectedExperienceId) {
      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "trip_data", "", bookingSelections?.date, bookingId, mode))
        .then(() => {
          setDurationLoading(false)
        })
        .catch(() => {
          setDurationLoading(false)
        })
    }
  }, [date, selectedExperienceId, dispatch, mode, bookingId])

  useEffect(() => {
    const tripLength = bookingSelections?.tripLength?.price?.id
    const tripDate = bookingSelections?.date

    // Create a unique key for this API call to prevent duplicate calls
    const callKey = `${selectedExperienceId}-${tripLength}-${tripDate}-${bookingId}`

    if (tripLength && tripDate && selectedExperienceId) {
      // Prevent duplicate calls with the same parameters
      if (lastSlotsFetchRef.current === callKey) {
        return
      }

      lastSlotsFetchRef.current = callKey
      setTimeLoading(true)

      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "available_slots", tripLength, tripDate, bookingId, mode))
        .then(() => {
          setTimeLoading(false)
        })
        .catch((error) => {
          console.error("Failed to fetch available slots:", error)
          setTimeLoading(false)
          // Clear time slot selection on error to prevent stale data
          dispatch(updateBookingSelections({ timeSlot: null }))
          form.setFieldsValue({ time: null })
        })
    } else {
      setTimeLoading(false)
      lastSlotsFetchRef.current = null
    }
  }, [bookingSelections?.tripLength?.price?.id, bookingSelections?.date, selectedExperienceId, dispatch, mode, bookingId, form])

  return (
    <Row gutter={16}>
      <Col xs={24} sm={24}>
        <Form.Item
          name="experience"
          label={t("booking.labels.experience")}
          rules={[{ required: true, message: t("booking.error_messages.experience_required") }]}
        >
          <Select
            labelInValue
            placeholder="Select Experience"
            showSearch
            onChange={handleExperienceChange}
            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            disabled={isEdit}
          >
            {filteredExperiences?.map((trip) => (
              <Option key={trip.id} value={trip.uuid} label={trip.title}>
                {trip.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24}>
        <Form.Item
          name="date"
          label={t("booking.labels.booked_date")}
          rules={[{ required: true, message: t("booking.error_messages.date_required") }]}
        >
          <DatePicker
            value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
            style={{ width: "100%" }}
            disabledDate={disableDates}
            onChange={handleDateChange}
            format="MM/DD/YYYY"
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24}>
        <Form.Item
          label={t("booking.labels.duration")}
          name="duration"
          rules={[{ required: true, message: t("booking.error_messages.duration_required") }]}
        >
          {durationLoading ? (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <Spin size="small" />
            </div>
          ) : (
            <TimeDurationSelector
              options={updatedDurationSlots || []}
              selectedValue={bookingSelections?.tripLength?.key}
              onChange={handleTripDurationChange}
            />
          )}
        </Form.Item>
      </Col>
      {bookingSelections?.tripLength && (
        <Col xs={24} sm={24}>
          <Form.Item
            label={t("booking.labels.booked_time")}
            name="time"
            rules={[{ required: true, message: t("booking.error_messages.time_required") }]}
          >
            {timeLoading ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <Spin size="small" />
              </div>
            ) : (
              <TimeDurationSelector
                options={updatedTimeSlots}
                selectedValue={bookingSelections?.timeSlot}
                onChange={(selected) => handleTimeSlotChange(selected.key)}
              />
            )}
          </Form.Item>
        </Col>
      )}
      {bookingSelections?.timeSlot && (
        <Col xs={24} sm={24}>
          <GuestInputGroup form={form} setCounts={handleGuestsChange} booking={booking} />
        </Col>
      )}
      <Col xs={24} sm={24}>
        <Form.Item name="accessibility_required" valuePropName="checked">
          <Space align="center">
            <Switch value={accessibilityRequired} onChange={(val) => form.setFieldsValue({ accessibility_required: val })} />
            <span>{t("booking.labels.accessibility_service")}</span>
          </Space>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24}>
        {accessibilityRequired && (
          <Form.Item
            name="accessibility_notes"
            rules={[{ required: accessibilityRequired, message: t("booking.error_messages.accessibility_notes_required") }]}
          >
            <TextArea placeholder={t("booking.labels.accessibility_notes")} rows={3} />
          </Form.Item>
        )}
      </Col>
      <Col xs={24} sm={24}>
        <Form.Item label={t("booking.labels.notes")} name="notes">
          <TextArea rows={3} />
        </Form.Item>
      </Col>
    </Row>
  )
}

ExperienceDetail.propTypes = {
  form: PropTypes.object.isRequired,
  bookingId: PropTypes.string.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  mode: PropTypes.string,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number) // Optional - for filtering by equipment
}

export default ExperienceDetail
